import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';

import { isEditorLive } from '@heidi/editor/core';
/**
 * Keeps the captured note selection visibly highlighted while the floating
 * Ask Heidi bar holds it as context (selection → Ask Heidi). Browsers stop
 * painting the native selection once the editor blurs (focus moves to the
 * floating bar), so this plugin re-paints the range as an inline decoration
 * in the same blue as the native highlight — to the user the selection
 * simply stays put.
 */
export const selectionContextHighlightKey = new PluginKey<DecorationSet>(
  'selectionContextHighlight'
);

interface HighlightRange {
  from: number;
  to: number;
}

/**
 * Class defined in `src/styles/globals.css` next to the global `::selection`
 * rule, using the same colour (bg-surface-selected-hover) so the pinned
 * highlight is indistinguishable from the live one. A class is used instead
 * of an inline `var()` style because style-attribute custom-property lookups
 * are brittle (jsdom drops them entirely; browsers blank the declaration if
 * the variable is invalid).
 */
export const SELECTION_CONTEXT_HIGHLIGHT_CLASS = 'selection-context-highlight';

export const createSelectionContextHighlightPlugin = () =>
  new Plugin<DecorationSet>({
    key: selectionContextHighlightKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, decorations) {
        const meta = tr.getMeta(selectionContextHighlightKey) as
          | HighlightRange
          | false
          | undefined;
        if (meta !== undefined) {
          if (meta === false) return DecorationSet.empty;
          const from = Math.max(0, Math.min(meta.from, tr.doc.content.size));
          const to = Math.max(from, Math.min(meta.to, tr.doc.content.size));
          if (from === to) return DecorationSet.empty;
          return DecorationSet.create(tr.doc, [
            Decoration.inline(from, to, { class: SELECTION_CONTEXT_HIGHLIGHT_CLASS }),
          ]);
        }
        // Keep the highlight anchored through unrelated document changes.
        return decorations.map(tr.mapping, tr.doc);
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });

/** Paint (or clear, with `null`) the pinned-context highlight in an editor. */
export const setSelectionContextHighlight = (
  editor: Editor,
  range: HighlightRange | null
): void => {
  if (!isEditorLive(editor, 'setSelectionContextHighlight')) return;
  const tr = editor.state.tr.setMeta(selectionContextHighlightKey, range ?? false);
  editor.view.dispatch(tr);
};
